/**
 * @file MeetingEvidenceInline.tsx
 * @brief Where an intel item was said, opened INSIDE its row on click.
 *
 * @details
 * Web / desktop twin of the phone's press-and-hold evidence sheet
 * (FRONTEND MeetingDetails/components/MeetingEvidenceSheet.tsx, 28-09-2026).
 * DES 05-10-2026: the play chip on every row goes, so nobody plays a clip by
 * accident; a CLICK on the row opens this panel under it (mouse users do not
 * press and hold, and on a desktop there is room to open it in place rather
 * than in a sheet over the page). A second click closes it, and closing stops
 * the clip because the play button unmounts with it.
 *
 * What it shows, same as the phone:
 * - when it was said ("About" when approximate) and who flagged it;
 * - the source line with two lines either side, speaker and time on each, the
 *   source line in an outline bubble with the anchor's verbatim quote
 *   highlighted;
 * - one Play button for the same clip the chip played.
 * DES 20-10-2026: laid out as a timeline (rail + dot per line, the bubble's
 * tail landing on the rail), each line in its own text direction (an Urdu
 * line, its name and time start at the right), Play + Close in the header.
 *
 * ⛔ Never a guessed source: an unanchored item says so, a hand-added to-do
 * says who added it, and an agenda point the meeting never raised says that.
 *
 * @references
 * - meetingEvidence.ts (resolveVoiceAnchor, anchorClipSecs, formatClock)
 * - MeetingDetailView.tsx (PhoneMomentRow, ActionItemRow, AgendaCoverageRow)
 *
 * @author DES
 * @date 05-10-2026
 */

import { useEffect, useState, type ReactNode } from "react";
import { Info, Pause, Play, Quote, X } from "lucide-react";
import { AudioPlayBadge } from "@/components/ui/AudioPlayer";
import { OutlineBubble } from "@/components/ui/OutlineBubble";
import { cn } from "@/shared/theme/cn";
import { dockGlassDark } from "@/shared/theme/dockGlass";
import { isRTLText } from "@/shared/utils/formatters";
import type { MeetingDetail } from "../schemas";
import { anchorClipSecs, formatClock, type VoiceAnchor } from "./meetingEvidence";
import { countWords, revealSegments, typingStepMs, type TypedSegment } from "./typedReveal";

type TranscriptRow = NonNullable<MeetingDetail["transcript"]>[number];

/** A line longer than this is shown as a window around the quote. */
const LONG_LINE_CHARS = 600;
/** Characters kept either side of the quote in a windowed line. */
const WINDOW_PAD_CHARS = 220;
/** Words kept from each end of the quote for the loose match. */
const EDGE_WORDS = 5;

export type EvidenceEmpty = "manual" | "not_raised" | "unanchored" | null;

/**
 * Opens and closes its content smoothly: height through grid rows 0fr -> 1fr
 * plus a fade, 200ms, and the content stays mounted until the close finishes.
 * Mounting the panel in one frame read as the page flicking (DES 05-10-2026),
 * partly because the outline bubble sizes itself after its first layout; the
 * fade hides that settle.
 */
export function EvidenceCollapse({ open, children }: { open: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setExpanded(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setExpanded(false);
    const t = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(t);
  }, [open]);
  if (!mounted) return null;
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none",
        expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/** Mirrors the phone's splitOnQuote: exact first, then first/last words. */
function splitOnQuote(text: string, quote: string | null) {
  if (!quote) return null;
  const lower = text.toLowerCase();
  const q = quote.toLowerCase().replace(/\s+/g, " ").trim();
  let at = lower.indexOf(q);
  let end = at >= 0 ? at + q.length : -1;
  if (at < 0) {
    const words = q.split(" ");
    if (words.length >= EDGE_WORDS * 2) {
      const head = words.slice(0, EDGE_WORDS).join(" ");
      const tail = words.slice(-EDGE_WORDS).join(" ");
      const h = lower.indexOf(head);
      const t = h >= 0 ? lower.indexOf(tail, h + head.length) : -1;
      if (h >= 0 && t >= 0) {
        at = h;
        end = t + tail.length;
      }
    }
  }
  if (at < 0 || end <= at) return null;
  return { before: text.slice(0, at), match: text.slice(at, end), after: text.slice(end) };
}

/** Keep a long line bounded, centred on the quote. */
function windowed(parts: { before: string; match: string; after: string }) {
  const total = parts.before.length + parts.match.length + parts.after.length;
  if (total <= LONG_LINE_CHARS) return parts;
  return {
    before:
      parts.before.length > WINDOW_PAD_CHARS
        ? `…${parts.before.slice(-WINDOW_PAD_CHARS)}`
        : parts.before,
    match: parts.match,
    after:
      parts.after.length > WINDOW_PAD_CHARS
        ? `${parts.after.slice(0, WINDOW_PAD_CHARS)}…`
        : parts.after,
  };
}

/**
 * The source line and two either side. Segment id first (what the anchor
 * stored), then the legacy `t_N` position, then the last line that started
 * at or before the moment. Same order as the phone.
 */
function sourceLines(
  transcript: MeetingDetail["transcript"],
  rawAnchor: Record<string, unknown> | null,
  anchor: VoiceAnchor,
): { row: TranscriptRow; focus: boolean }[] {
  const rows = transcript ?? [];
  const segId = rawAnchor?.segmentId ? String(rawAnchor.segmentId) : null;
  let focus = segId ? rows.findIndex((r) => r?.id === segId) : -1;
  if (focus < 0 && segId) {
    const m = /^t_(\d+)$/.exec(segId);
    const n = m ? Number(m[1]) - 1 : -1;
    if (n >= 0 && n < rows.length && !rows[n]?.id) focus = n;
  }
  if (focus < 0) {
    for (let i = 0; i < rows.length; i += 1) {
      const st = rows[i]?.startSecs;
      if (typeof st === "number" && st <= anchor.markerSecs + 0.001) focus = i;
      else if (typeof st === "number") break;
    }
  }
  if (focus < 0) return [];
  return rows
    .slice(Math.max(0, focus - 2), focus + 3)
    .map((row) => ({ row, focus: row === rows[focus] }));
}

export function MeetingEvidenceInline({
  anchor,
  rawAnchor,
  transcript,
  recordingUrl,
  empty = null,
  manualBy = null,
  onClose,
  className,
  heading = "Where it was said",
}: {
  /** Resolved playback span, or null when the item could not be located. */
  anchor: VoiceAnchor | null;
  /** The stored anchor object (segmentId, quote, flaggedBy). */
  rawAnchor: unknown;
  transcript: MeetingDetail["transcript"];
  recordingUrl: string | null | undefined;
  /** Set for a hand-added to-do or an agenda point never raised. */
  empty?: Exclude<EvidenceEmpty, "unanchored">;
  manualBy?: string | null;
  /** The close control in the panel's corner. */
  onClose?: () => void;
  className?: string;
  /** B49: an agenda tick's moment reads "When it was ticked". */
  heading?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const raw =
    rawAnchor && typeof rawAnchor === "object" ? (rawAnchor as Record<string, unknown>) : null;
  const flaggedBy = Array.isArray(raw?.flaggedBy) ? (raw.flaggedBy as unknown[]).map(String) : [];
  const state: EvidenceEmpty = empty ?? (anchor ? null : "unanchored");

  const emptyMessage =
    state === "manual"
      ? manualBy
        ? `Added by ${manualBy}. Not from the recording.`
        : "Added by hand. Not from the recording."
      : state === "not_raised"
        ? "This agenda point was not raised in the meeting."
        : state === "unanchored"
          ? "No source in the transcript. We could not match this to a moment, so we will not show a guess."
          : null;

  const quote = typeof raw?.quote === "string" && raw.quote.trim() ? raw.quote.trim() : null;
  const lines = !state && anchor ? sourceLines(transcript, raw, anchor) : [];
  const canPlay = !state && !!anchor && !!recordingUrl;

  // The lines' span for the header ("22:06 – 23:38").
  const firstTime = lines[0]?.row?.timestamp ? String(lines[0].row.timestamp) : "";
  const lastTime = lines.length > 1 && lines[lines.length - 1]?.row?.timestamp
    ? String(lines[lines.length - 1].row.timestamp)
    : "";

  return (
    <div
      // Clicks inside the panel (play, selecting text) must not close it.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      // No grey box (DES 20-10-2026): the timeline sits straight on the page.
      className={cn("mt-3 space-y-4 py-1", className)}
    >
      {/* Header: what this is, the span, and Play + Close together on top
          (DES 20-10-2026: the button no longer sits under the lines). */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <Quote className="h-4 w-4 text-brand" strokeWidth={2} />
          <span className="text-[13px] font-semibold text-foreground">{heading}</span>
          {anchor && !state ? (
            <span>
              {"· "}
              {firstTime && lastTime
                ? `${firstTime} – ${lastTime}`
                : anchor.confidence === "approx"
                  ? `About ${formatClock(anchor.markerSecs)}`
                  : formatClock(anchor.markerSecs)}
            </span>
          ) : null}
          {flaggedBy.length > 0 && !state ? <span>· Flagged by {flaggedBy.join(", ")}</span> : null}
        </p>
        <div className="flex items-center gap-1.5">
          {canPlay && anchor ? (
            <AudioPlayBadge
              src={recordingUrl}
              variant="original"
              startSecs={Math.max(0, anchor.startSecs)}
              clipSecs={anchorClipSecs(anchor)}
              label={
                playing
                  ? "Stop"
                  : `Play ${anchor.confidence === "approx" ? "about " : ""}${formatClock(anchor.markerSecs)}`
              }
              icon={playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              onPlayingChange={setPlaying}
              // Dark: THE dock glass, red glyph, primary-text label (phone
              // VZButton `darkGlass`, DES 08-10-2026); light keeps the red pill.
              className={cn(
                "h-8 rounded-full border-transparent bg-brand px-3.5 text-[13px] font-semibold normal-case tracking-normal text-brand-foreground hover:bg-brand/90",
                dockGlassDark,
                "dark:text-app-text dark:[&_svg]:text-brand",
              )}
              playingClassName="bg-brand text-brand-foreground dark:text-app-text"
            />
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              title="Close"
              aria-label="Close where it was said"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/[0.04] text-muted-foreground transition-colors hover:bg-black/[0.08] hover:text-foreground dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {emptyMessage ? (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {emptyMessage}
        </p>
      ) : lines.length === 0 ? (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          The transcript line could not be found, but the moment can still be played.
        </p>
      ) : (
        // The timeline (DES 20-10-2026): a rail down the start side with a
        // dot per line; the source line is our outline bubble, whose tail and
        // dot land on the rail. Each line follows its own text's direction:
        // an Urdu line starts at the right, name and time included.
        <ol>
          {lines.map(({ row, focus }, i) => {
            const text = String(row?.text ?? "");
            const split = focus ? splitOnQuote(text, quote) : null;
            const parts = split ? windowed(split) : null;
            const plain =
              !parts && text.length > LONG_LINE_CHARS ? `${text.slice(0, LONG_LINE_CHARS)}…` : text;
            const rtl = isRTLText(text);
            const { name, tag } = speakerParts(String(row?.speaker ?? "Speaker"));
            const time = row?.timestamp ? String(row.timestamp) : "";
            const last = i === lines.length - 1;
            const body = (
              <p
                dir={rtl ? "rtl" : "ltr"}
                className={cn(
                  "leading-relaxed",
                  rtl ? "text-right" : "text-left",
                  // Context lines stay well back so the bubble leads (DES 20-10-2026).
                  focus ? "text-[15px] text-foreground" : "line-clamp-3 text-sm text-muted-foreground/55",
                )}
              >
                {focus ? (
                  // Types in word by word as the panel opens (DES 20-10-2026).
                  <TypedText
                    segments={
                      parts
                        ? [
                            { text: parts.before, marked: false },
                            { text: parts.match, marked: true },
                            { text: parts.after, marked: false },
                          ]
                        : [{ text: plain, marked: false }]
                    }
                  />
                ) : (
                  plain
                )}
              </p>
            );
            if (focus) {
              return (
                <li key={row?.id ?? `l${i}`} className={cn("relative pl-[5px]", !last && "pb-3")}>
                  {!last ? <Rail from="calc(100% - 10px)" /> : null}
                  <OutlineBubble
                    side="start"
                    labelSide={rtl ? "end" : "start"}
                    className="px-4 py-3 [--bubble:hsl(var(--brand))]"
                    topLabel={
                      <span className="text-[12px] font-semibold">
                        {name}
                        {tag ? <span className="font-normal text-muted-foreground"> · {tag}</span> : null}
                      </span>
                    }
                    bottomLabel={time ? <span className="text-[12px] font-semibold text-brand">{time}</span> : undefined}
                  >
                    {body}
                  </OutlineBubble>
                </li>
              );
            }
            return (
              <li key={row?.id ?? `l${i}`} className={cn("relative pl-7", !last && "pb-4")}>
                {!last ? <Rail from="16px" /> : null}
                <span
                  aria-hidden
                  className="absolute left-0 top-1 h-3 w-3 rounded-full border-[1.5px] border-muted-foreground/35 bg-background"
                />
                <p
                  dir={rtl ? "rtl" : "ltr"}
                  className="mb-0.5 flex min-w-0 items-center gap-2 text-[12px] text-muted-foreground/55"
                >
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-[10px] font-semibold dark:bg-white/[0.08]">
                    {initials(name)}
                  </span>
                  <span className="truncate font-medium">{name}</span>
                  {tag ? (
                    <span className="shrink-0 rounded-full border border-border px-1.5 text-[11px]">{tag}</span>
                  ) : null}
                  {time ? <span className="shrink-0 tabular-nums">{time}</span> : null}
                </p>
                {body}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/**
 * The bubble's words typing in, one word a step, about 1.4 s in all. What is
 * still to come is drawn transparent, so the bubble has its final size from
 * the first frame. Reduced motion: all at once.
 */
function TypedText({ segments }: { segments: TypedSegment[] }) {
  const total = countWords(segments);
  const [shown, setShown] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ? total
      : 0,
  );
  useEffect(() => {
    if (shown >= total) return;
    const step = typingStepMs(total);
    const t = setInterval(() => {
      setShown((n) => {
        const next = n + 1;
        if (next >= total) clearInterval(t);
        return next;
      });
    }, step);
    return () => clearInterval(t);
    // Once per open: the panel mounts afresh each time it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);
  return (
    <>
      {revealSegments(segments, shown).map((seg, i) =>
        seg.marked ? (
          <span key={i}>
            {seg.visible ? (
              <mark className="rounded bg-brand/15 px-0.5 text-foreground">{seg.visible}</mark>
            ) : null}
            {seg.hidden ? <span className="text-transparent">{seg.hidden}</span> : null}
          </span>
        ) : (
          <span key={i}>
            {seg.visible}
            {seg.hidden ? <span className="text-transparent">{seg.hidden}</span> : null}
          </span>
        ),
      )}
    </>
  );
}

/** The rail from this line's dot down to the next line's. */
function Rail({ from }: { from: string }) {
  return (
    <span
      aria-hidden
      className="absolute left-[5px] w-[1.5px] bg-muted-foreground/30"
      style={{ top: from, bottom: 0 }}
    />
  );
}

/** "Dawood Ellahi (Client)" -> name + "Client" as a small tag. */
function speakerParts(speaker: string): { name: string; tag: string | null } {
  const m = /^(.*?)\s*\(([^()]{1,24})\)\s*$/.exec(speaker.trim());
  return m && m[1] ? { name: m[1], tag: m[2] } : { name: speaker.trim(), tag: null };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = (parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts.slice(0, 1))
    .map((p) => Array.from(p)[0] ?? "")
    .join("");
  return letters.toUpperCase() || "?";
}
